/**
 * COME SI SCRIVONO quote, ore e importi: in un posto solo.
 *
 * Tre funzioni che leggono le preferenze di chi guarda (preferenze.js) e
 * restituiscono la stringa da mostrare. Il valore che ricevono e' sempre
 * quello VERO del server: quota decimale, istante ISO in UTC, importo nella
 * valuta della skin. Qui non si fa nessun conto, solo la scrittura.
 *
 * Chi mostra una quota chiama mostraQuota(d) e basta: non sa in che formato
 * uscira', e non deve saperlo. Lo stesso per le ore e i soldi.
 */
import {scriviQuota, decimale} from './formatoQuote';
import {formatoQuota, fuso, lingua} from '../preferenze';
import {valuta} from '../skin';

/** La quota nel formato di chi guarda. */
export function mostraQuota(d) {
  return scriviQuota(d, formatoQuota());
}

/**
 * DUE QUOTE MESSE A CONFRONTO, scritte nel formato di chi guarda.
 * Torna [a, b], gia' scritte.
 *
 * Serve dove due quote stanno una accanto all'altra perche' chi legge le
 * confronti: il prima e il dopo di una riproposta, le quote cambiate di una
 * prenotazione riaperta, una quota e la soglia che non raggiunge. Chi le
 * scrive con mostraQuota e basta cade in una trappola: non tutti i formati
 * scrivono ogni quota in modo diverso. Il MALESE ha due cifre sole, e sulle
 * quote lunghe non bastano: 34.00 e 41.00 sono tutte e due «-0.03». Un
 * cambio scritto «-0.03 -> -0.03» dice al cliente che non e' cambiato niente
 * mentre la quota e' scesa. (Il frazionario aveva lo stesso difetto finche'
 * cercava la frazione piu' vicina; adesso e' esatto e non ci cade piu'.)
 *
 * LA REGOLA: due numeri diversi non si scrivono mai uguali nello stesso
 * confronto. Quando succederebbe -- e solo allora -- accanto a tutti e due
 * si mette il decimale, che e' il prezzo vero su cui si fanno i conti:
 * «-0.03 (41.00)» e «-0.03 (34.00)».
 *
 * COSA NON FA: non decide se il cambio c'e' (lo dice chi chiama, sui
 * decimali) e non tocca i conti.
 */
export function mostraCambioQuota(prima, dopo) {
  const a = mostraQuota(prima);
  const b = mostraQuota(dopo);
  if (!a || !b || a !== b || Number(prima) === Number(dopo)) return [a, b];
  return [`${a} (${decimale(prima)})`, `${b} (${decimale(dopo)})`];
}

/* GLI SCRITTORI DI DATE SI TENGONO DA PARTE. Costruire un Intl.DateTimeFormat
   costa molto piu' che usarlo, e ogni riga delle quote ne costruiva due (ora
   e giorno) a ogni disegno: su un telefono lento si sentiva a ogni tocco. Si
   ricordano per lingua + opzioni, che a pagina aperta sono una manciata. */
const scrittori = new Map();
function scrittoreDate(lingua, opzioni) {
  const chiave = lingua + '|' + JSON.stringify(opzioni);
  let f = scrittori.get(chiave);
  if (!f) {
    f = new Intl.DateTimeFormat(lingua, opzioni);
    scrittori.set(chiave, f);
  }
  return f;
}

/**
 * L'ora nel fuso di chi guarda.
 * forma: 'ora' (20:45), 'giorno-ora' (6/10 20:45), 'completa' (6/10/2026 20:45)
 * Con `sigla: true` aggiunge la sigla del fuso (CEST, BRT, GMT+3), che e'
 * obbligatoria dove l'ora decide qualcosa - ricevute, accettazione.
 */
export function formattaOra(iso, forma = 'ora', opzioni = {}) {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const zona = opzioni.fuso || fuso();
  const base = {timeZone: zona, hour: '2-digit', minute: '2-digit', hour12: false};
  let opt = base;
  if (forma === 'giorno-ora') opt = {...base, day: 'numeric', month: 'numeric'};
  if (forma === 'completa') opt = {...base, day: 'numeric', month: 'numeric', year: 'numeric'};
  if (forma === 'giorno') opt = {timeZone: zona, day: 'numeric', month: 'numeric'};
  if (opzioni.sigla) opt = {...opt, timeZoneName: 'short'};
  try {
    // L'ordine giorno/mese segue la lingua di chi guarda (7/10 in italiano,
    // 10/7 in inglese americano); l'ora resta a 24 ore per tutti.
    return scrittoreDate(opzioni.lingua || lingua(), opt).format(d).replace(',', '');
  } catch (e) {
    return '';
  }
}

/**
 * IL GIORNO PER ESTESO, nella lingua e nel fuso di chi guarda: «giovedì 9
 * ottobre», «Thursday 9 October», «jueves 9 de octubre». E' la fascia che
 * divide le partite per giornata nella griglia delle quote; prima la
 * scriveva moment in inglese per tutti.
 */
export function formattaGiorno(iso, opzioni = {}) {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    const testo = scrittoreDate(opzioni.lingua || lingua(), {
      timeZone: opzioni.fuso || fuso(), weekday: 'long', day: 'numeric', month: 'long',
    }).format(d);
    // l'iniziale maiuscola come in un titolo: Intl la scrive minuscola in molte lingue
    return testo.charAt(0).toUpperCase() + testo.slice(1);
  } catch (e) {
    return '';
  }
}

/** La sigla del fuso di chi guarda (CEST, BRT...). */
export function siglaFuso(iso) {
  const d = iso ? new Date(iso) : new Date();
  try {
    const parti = scrittoreDate(lingua(), {timeZone: fuso(), timeZoneName: 'short'})
        .formatToParts(d);
    const p = parti.find((x) => x.type === 'timeZoneName');
    return p ? p.value : '';
  } catch (e) {
    return '';
  }
}

/**
 * Oggi, domani e la mezzanotte di chi guarda non sono quelle del server: i
 * confronti "e' oggi?" si fanno sulla data scritta nel fuso di chi guarda.
 */
export function stessoGiorno(isoA, isoB) {
  const g = (iso) => formattaOra(iso, 'giorno');
  return g(isoA) !== '' && g(isoA) === g(isoB);
}

/**
 * Un importo nella valuta della skin, con i decimali giusti per quella
 * valuta (lo yen zero, il dinaro tre) e il simbolo al posto giusto.
 * La scrittura dei numeri (1.234,56 o 1,234.56) e il posto del simbolo
 * seguono la lingua di chi guarda.
 */
export function formattaImporto(n, opzioni = {}) {
  const v = Number(n || 0);
  const codice = opzioni.valuta || valuta();
  try {
    return new Intl.NumberFormat(opzioni.lingua || lingua(), {
      style: 'currency', currency: codice, currencyDisplay: 'symbol',
    }).format(v);
  } catch (e) {
    return v.toFixed(2) + ' ' + codice;
  }
}
